/* eslint-disable @typescript-eslint/no-explicit-any */
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import { Control, Controller, FieldErrors, FieldValues } from 'react-hook-form';
import { passwordName, userName } from '../../common/constants';
import {
  PasswordValidationRules,
  emailRules,
} from '../../common/formValidationRules';
import usePasswordVisibility from '../../hooks/usePasswordVisibility';
import ForgotPasswordLink from './ForgotPasswordLink';

interface LoginFormProps {
  control: Control<FieldValues> | Control<FieldValues, any>;
  errors: FieldErrors<FieldValues>;
  loading: boolean;
  showForgotPassword: boolean;
  passwordValidationRules?: PasswordValidationRules;
}

export default function LoginForm({
  control,
  errors,
  loading,
  showForgotPassword,
  passwordValidationRules = { required: true },
}: LoginFormProps) {
  const theme = useTheme();
  const { visible, toggle } = usePasswordVisibility();
  return (
    <>
      <Controller
        name={userName}
        control={control}
        defaultValue={''}
        rules={{ required: true, ...emailRules() }}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            label={'Email'}
            variant="filled"
            InputLabelProps={{
              shrink: true,
              style: {
                color: errors[userName]
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
              },
            }}
            disabled={loading}
            error={!!errors[userName]}
            helperText={errors[userName]?.message as string}
          />
        )}
      />

      <Controller
        name={passwordName}
        control={control}
        defaultValue={''}
        rules={passwordValidationRules}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            label={'Password'}
            variant="filled"
            type={visible ? 'text' : 'password'}
            slotProps={{
              inputLabel: {
                shrink: true,
                style: {
                  color: errors[passwordName]
                    ? theme.palette.error.main
                    : theme.palette.primary.main,
                },
              },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={toggle}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                    >
                      {visible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            disabled={loading}
            error={!!errors[passwordName]}
            helperText={errors[passwordName]?.message as string}
          />
        )}
      />

      {showForgotPassword && <ForgotPasswordLink />}
    </>
  );
}
